import React from 'react'
import { DISMISSAL_KEYS, DISMISSAL_OPTIONS, STAT_GROUPS, YES_NO_KEYS } from './scorecardConfig'
import { normalize } from 'src/components/common/playerMatching'

// Reading scorecard figures out of a spreadsheet. Split out of the scorecard import once the
// squad import needed it too: a match export dropped on the squad screen carries the same
// figures, and both screens have to read them the same way.
//
// A file names its columns the way the grid labels them - RS, 4S, WKTS - while the table stores
// them under attribute keys - rs, fours, wks. The label, the key and the full name all come from
// the same FixtureAttribute record, so a column is matched on any of the three and written back
// under its key.

const TRUE_TOKENS = new Set(['1', 'y', 'yes', 'true', 't'])
const FALSE_TOKENS = new Set(['0', 'n', 'no', 'false', 'f'])

// Every way a dismissal can be written: the stored code, the label on the grid's dropdown, and
// the words scorers actually type.
const DISMISSAL_LOOKUP = new Map([
  ...DISMISSAL_OPTIONS.filter((o) => o.value).flatMap((o) => [
    [normalize(o.value), o.value],
    [normalize(o.label), o.value],
  ]),
  ['b', 'hobwl'],
  ['bowled', 'hobwl'],
  ['caught', 'hoct'],
  ['c', 'hoct'],
  ['ct', 'hoct'],
  ['caughtbehind', 'hocwk'],
  ['caughtkeeper', 'hocwk'],
  ['lbw', 'holbw'],
  ['stumped', 'host'],
  ['st', 'host'],
  ['runout', 'horod'],
  ['runoutdirect', 'horod'],
  ['runoutassisted', 'horoa'],
  ['hitwicket', 'hohw'],
  ['handledball', 'hohb'],
  ['obstructingthefield', 'hoobs'],
  ['timedout', 'hotmd'],
  ['notout', ''],
  ['no', ''],
  ['dnb', ''],
])

// Words an external scorecard is likely to use for a column this game calls something else.
// Matched only after attribute_key, label and full_name have all missed.
const COLUMN_ALIASES = {
  rs: ['runs', 'runsscored', 'r'],
  bf: ['balls', 'ballsfaced'],
  fours: ['4s', 'fours'],
  sixes: ['6s', 'sixes'],
  overs: ['ovrs', 'oversbowled', 'o'],
  mdns: ['maidens', 'm'],
  run: ['runsgiven', 'runsconceded', 'rg'],
  wks: ['wickets', 'wkts', 'w'],
  cs: ['catches'],
  cwks: ['catcheswk', 'catcheskeeper'],
  sts: ['stumpings'],
  rods: ['runoutsdirect'],
  roas: ['runoutsassisted'],
  dks: ['duck'],
  no: ['notout'],
  hattrick: ['hattrick'],
  hwot: ['howout', 'dismissal', 'dismissedas'],
}

// Reads one cell against the kind of column it belongs to. A blank cell is deliberately not a
// zero - it means "leave whatever is on the scorecard alone", so a file with only bowling
// figures in it cannot wipe the batting side of the innings.
export const parseCellValue = (raw, attributeKey) => {
  const text = String(raw ?? '').trim()
  if (text === '') return { skip: true }
  const token = normalize(text)

  if (YES_NO_KEYS.includes(attributeKey)) {
    if (TRUE_TOKENS.has(token)) return { value: 1 }
    if (FALSE_TOKENS.has(token)) return { value: 0 }
    return { invalid: true }
  }

  if (DISMISSAL_KEYS.includes(attributeKey)) {
    const mapped = DISMISSAL_LOOKUP.get(token)
    if (mapped === undefined) return { invalid: true }
    return { value: mapped }
  }

  const number = Number(text.replace(/,/g, ''))
  if (!Number.isFinite(number)) return { invalid: true }
  return { value: number }
}

// Shows a parsed value the way the grid would: Yes/No and dismissal labels rather than the
// codes they are stored as.
export const DisplayValue = ({ attributeKey, value }) => {
  if (value === undefined) return <span className="text-medium-emphasis">&mdash;</span>
  if (YES_NO_KEYS.includes(attributeKey)) return <>{Number(value) === 1 ? 'Yes' : 'No'}</>
  if (DISMISSAL_KEYS.includes(attributeKey)) {
    const option = DISMISSAL_OPTIONS.find((o) => o.value === value)
    return <>{option?.label || <span className="text-medium-emphasis">&mdash;</span>}</>
  }
  return <>{value}</>
}

// A match export carries the whole grid, so a row's figures are read out banded by discipline
// the way the grid itself bands its columns, rather than as one long run of codes. Only the
// bands the file actually filled in are shown.
export const RowScores = ({ statColumns, values }) => {
  const bands = STAT_GROUPS.map((group) => ({
    label: group.label,
    columns: statColumns.filter(
      (column) =>
        group.keys.includes(column.attribute_key) && values[column.attribute_key] !== undefined,
    ),
  }))
  const banded = new Set(STAT_GROUPS.flatMap((group) => group.keys))
  const rest = statColumns.filter(
    (column) => !banded.has(column.attribute_key) && values[column.attribute_key] !== undefined,
  )
  if (rest.length) bands.push({ label: 'Other', columns: rest })

  const filled = bands.filter((band) => band.columns.length)
  if (!filled.length) return <span className="small text-medium-emphasis">No scores</span>

  return (
    <div className="small" style={{ minWidth: 220 }}>
      {filled.map((band) => (
        <div key={band.label} className="d-flex gap-1 flex-wrap">
          <span className="text-medium-emphasis" style={{ minWidth: 54 }}>
            {band.label}
          </span>
          {band.columns.map((column) => (
            <span key={column.attribute_key} className="text-nowrap">
              <span className="text-medium-emphasis">{column.label}</span>{' '}
              <DisplayValue
                attributeKey={column.attribute_key}
                value={values[column.attribute_key]}
              />
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}

// Scorecard Import: upload a spreadsheet of this innings' figures, map every row onto a player
// already in the squad - by name where the file and the squad agree, by hand where they do not -
// then write only the rows left included. Same 3-step shape as the Squad and Fixture imports.
// Claims the headers that name a stat this game scores, out of whatever is left after the
// player columns have been taken. Matching is exact against the key, the label or the full name
// (a loose match would read "Runs Given" as "Runs"), with COLUMN_ALIASES for the words an
// outside scorecard is likely to use instead.
//
// A real match export carries every column on the grid whether the innings used it or not, so a
// column that is blank the whole way down is claimed but dropped - it has nothing to import, and
// showing it costs the admin two dozen empty cells to scroll past.
export const takeStatColumns = (pool, json, scorecardColumns) => {
  const detected = []
  ;(scorecardColumns || []).forEach((column) => {
    if (!column?.attribute_key) return
    const keys = [
      normalize(column.attribute_key),
      normalize(column.label),
      normalize(column.full_name),
      ...(COLUMN_ALIASES[column.attribute_key] || []),
    ].filter(Boolean)
    const header = pool.find((h) => keys.includes(normalize(h)))
    if (!header) return
    const used = json.some((r) => String(r[header] ?? '').trim() !== '')
    pool.splice(pool.indexOf(header), 1)
    if (used) detected.push({ ...column, header })
  })
  return detected
}

// The raw cells for one sheet row, keyed by attribute key so a row survives re-validation
// without the original headers.
export const readStatCells = (sheetRow, statColumns) => {
  const cells = {}
  statColumns.forEach((column) => {
    cells[column.attribute_key] = sheetRow[column.header]
  })
  return cells
}

// Turns those raw cells into what the scorecard stores, counting the ones that could not be read
// rather than guessing at them.
export const parseStatCells = (cells, statColumns) => {
  const values = {}
  let invalidCells = 0
  statColumns.forEach((column) => {
    const parsed = parseCellValue(cells?.[column.attribute_key], column.attribute_key)
    if (parsed.skip) return
    if (parsed.invalid) {
      invalidCells += 1
      return
    }
    values[column.attribute_key] = parsed.value
  })
  return { values, invalidCells }
}
